import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import { Info, LayoutGrid, Search, Loader2, Filter, ChevronDown, Shield } from 'lucide-react';
import { formatPreferCrores, formatIndianCurrency } from '../utils/currency.ts';

type SortDir = 'ASC' | 'DESC';
type DistColKey =
  | 'user'
  | 'email'
  | 'achieved'
  | 'company80'
  | 'totalKitty'
  | 'userKitty'
  | 'deptKitty'
  | 'companyKitty';

const DIST_COL_OPTIONS: { key: DistColKey; label: string }[] = [
  { key: 'user', label: 'User' },
  { key: 'achieved', label: 'Achieved Value' },
  { key: 'company80', label: '80% Company' },
  { key: 'totalKitty', label: 'Total Kitty Pool (20%)' },
  { key: 'userKitty', label: 'Contributor Share (15%)' },
  { key: 'deptKitty', label: 'Department Kitty (15%)' },
  { key: 'companyKitty', label: 'Company Kitty (70%)' },
];

const DEFAULT_DIST_COLS: Record<DistColKey, boolean> = {
  user: true,
  email: false,
  achieved: true,
  company80: false,
  totalKitty: true,
  userKitty: true,
  deptKitty: true,
  companyKitty: true,
};

/** User-Wise: same as Distribution, Dept Kitty hidden */
const DEFAULT_USER_DIST_COLS: Record<DistColKey, boolean> = {
  ...DEFAULT_DIST_COLS,
  deptKitty: false,
};

/** Department-Wise: same as Distribution, User Kitty hidden */
const DEFAULT_DEPT_DIST_COLS: Record<DistColKey, boolean> = {
  ...DEFAULT_DIST_COLS,
  userKitty: false,
};

function amountSearchText(...amounts: number[]): string {
  return amounts
    .flatMap((a) => [
      String(a),
      formatPreferCrores(a),
      formatIndianCurrency(a),
      (a / 10_000_000).toFixed(2),
      (a / 100_000).toFixed(2),
      (a / 1_000).toFixed(2),
    ])
    .join(' ')
    .toLowerCase();
}

interface MetrixAmountDisplayProps {
  amount: number;
  label?: string;
  textSize?: string;
  textColor?: string;
  forceCrores?: boolean;
  align?: 'left' | 'center' | 'right';
}

function MetrixAmountDisplay({
  amount,
  label = 'Exact Amount',
  textSize = 'text-xs font-bold',
  textColor,
  forceCrores = true,
  align = 'center',
}: MetrixAmountDisplayProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number; arrowLeft: number } | null>(null);
  const [resolvedPlacement, setResolvedPlacement] = useState<'top' | 'bottom'>('bottom');
  const containerRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const rect = el.getBoundingClientRect();
    const tooltipW = 180;
    const tooltipH = 75;
    const gap = 8;

    let parent: HTMLElement | null = el.parentElement;
    let scrollParent: HTMLElement | null = null;
    while (parent) {
      const style = getComputedStyle(parent);
      if (/(auto|scroll)/.test(style.overflowY) || /(auto|scroll)/.test(style.overflow)) {
        scrollParent = parent;
        break;
      }
      parent = parent.parentElement;
    }

    const scrollBounds = scrollParent?.getBoundingClientRect();

    const thead = scrollParent?.querySelector('thead');
    const theadBottom = thead
      ? thead.getBoundingClientRect().bottom
      : scrollBounds
        ? scrollBounds.top
        : 0;

    const tfoot = scrollParent?.querySelector('tfoot');
    const tfootTop = tfoot
      ? tfoot.getBoundingClientRect().top
      : scrollBounds
        ? scrollBounds.bottom
        : window.innerHeight;

    const spaceAbove = rect.top - Math.max(0, theadBottom);
    const spaceBelow = Math.min(window.innerHeight, tfootTop) - rect.bottom;

    let place: 'top' | 'bottom';
    if (spaceBelow >= tooltipH + gap) {
      place = 'bottom';
    } else if (spaceAbove >= tooltipH + gap) {
      place = 'top';
    } else {
      place = spaceBelow >= spaceAbove ? 'bottom' : 'top';
    }

    setResolvedPlacement(place);

    const top = place === 'bottom' ? rect.bottom + gap : rect.top - gap;
    const targetCenterX = rect.left + rect.width / 2;
    const desiredLeft = targetCenterX - tooltipW / 2;
    const left = Math.max(16, Math.min(window.innerWidth - tooltipW - 16, desiredLeft));
    const arrowLeft = Math.max(12, Math.min(tooltipW - 24, targetCenterX - left - 6));

    setCoords({ top, left, arrowLeft });
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    function handleScrollOrResize() {
      setIsOpen(false);
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (isOpen) {
      updatePosition();
    }
  }, [isOpen]);

  const formatDisplayAmount = (num: number): string => {
    if (!num) return '0 Cr';
    const abs = Math.abs(num);
    const sign = num < 0 ? '-' : '';

    const crVal = abs >= 10000 ? abs / 10000000 : abs;
    const formatted = crVal.toFixed(2).replace(/\.00$/, '');
    return `${sign}${formatted} Cr`;
  };

  const formattedValue = forceCrores ? formatDisplayAmount(amount) : formatIndianCurrency(amount);
  const fullAmountString = `₹${(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

  const alignClass =
    align === 'center'
      ? 'justify-center'
      : align === 'left'
        ? 'justify-start'
        : 'justify-end';

  const defaultTextColor = textColor || 'text-slate-800';

  return (
    <div
      className="relative inline-block max-w-full"
      ref={containerRef}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <div
        className={`flex items-center space-x-1 cursor-pointer group min-w-0 ${alignClass}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <p className={`${textSize} ${defaultTextColor} whitespace-nowrap transition-colors group-hover:text-blue-600`}>
          ₹{formattedValue}
        </p>
        <div
          className={`p-0.5 rounded-full shrink-0 transition-colors ${isOpen ? 'bg-blue-50' : 'group-hover:bg-slate-100'
            }`}
        >
          <Info
            className={`w-3.5 h-3.5 flex-shrink-0 transition-colors ${isOpen ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'
              }`}
          />
        </div>
      </div>

      {isOpen &&
        coords &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              transform: resolvedPlacement === 'top' ? 'translateY(-100%)' : 'none',
              zIndex: 99999,
            }}
            className="p-3 bg-slate-900 text-white rounded-xl shadow-2xl min-w-[170px] whitespace-nowrap animate-in fade-in zoom-in-95 duration-150 border border-slate-700 pointer-events-none text-left"
          >
            <p className="text-[11px] text-slate-300 font-semibold tracking-wide mb-0.5">{label}</p>
            <p className="text-lg font-bold tracking-wide text-white">{fullAmountString}</p>
            <div
              style={{ left: coords.arrowLeft }}
              className={`absolute w-3 h-3 bg-slate-900 border-slate-700 transform rotate-45 ${resolvedPlacement === 'bottom'
                ? '-top-1.5 border-t border-l'
                : '-bottom-1.5 border-b border-r'
                }`}
            />
          </div>,
          document.body
        )}
    </div>
  );
}

type MatrixRow = {
  id: string;
  userId: string;
  userName: string;
  userEmail?: string;
  ideaName: string;
  department: string;
  approvedAmount: number;
  companyDirectAmount: number;
  incentivePoolAmount: number;
  commissionAmount: number;
  departmentAmount: number;
  companyPoolShareAmount: number;
  companyTotalAmount: number;
  ideasCount?: number;
};

type MatrixDepartment = {
  department: string;
  usersCount: number;
  ideasCount: number;
  amount: number;
};

type MatrixData = {
  formula: {
    incentivePoolRate: number;
    userOfPoolRate: number;
    departmentOfPoolRate: number;
    description: string;
  };
  summary: {
    ideasCount: number;
    usersCount: number;
    departmentsCount: number;
    totalApprovedAmount: number;
    totalCommission: number;
    totalDepartmentAmount: number;
  };
  rows: MatrixRow[];
  departments: MatrixDepartment[];
};


function SortToggle({ value, onChange }: { value: SortDir; onChange: (v: SortDir) => void }) {
  const apply = (next: SortDir) => {
    if (next === value) return;
    const y = window.scrollY;
    onChange(next);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => window.scrollTo({ top: y }));
    });
  };
  return (
    <div className="bg-slate-100 p-0.5 rounded-xl flex items-center border border-slate-200 shrink-0">
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => apply('ASC')}
        className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all cursor-pointer ${value === 'ASC' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
          }`}
      >
        Low → High
      </button>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => apply('DESC')}
        className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all cursor-pointer ${value === 'DESC' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
          }`}
      >
        High → Low
      </button>
    </div>
  );
}

function DeptFilter({
  id,
  selected,
  setSelected,
  search,
  setSearch,
  containerRef,
  allDepartments,
  openDeptFilter,
  setOpenDeptFilter,
}: {
  id: 'dist' | 'user' | 'dept';
  selected: string[];
  setSelected: (v: string[]) => void;
  search: string;
  setSearch: (v: string) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
  allDepartments: string[];
  openDeptFilter: 'dist' | 'user' | 'dept' | null;
  setOpenDeptFilter: (v: 'dist' | 'user' | 'dept' | null) => void;
}) {
  const isOpen = openDeptFilter === id;
  const filtered = allDepartments.filter((c) => c.toLowerCase().includes(search.toLowerCase()));
  return (
    <div ref={containerRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpenDeptFilter(isOpen ? null : id)}
        className={`border rounded-xl px-2.5 py-1.5 text-[10px] font-bold focus:outline-none cursor-pointer shadow-sm flex items-center justify-between gap-1.5 min-w-[140px] max-w-[200px] transition-all ${selected.length > 0
          ? 'border-slate-300 bg-slate-50 text-slate-900'
          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
          }`}
      >
        <div className="flex items-center gap-1.5 truncate">
          <Filter className={`w-3 h-3 shrink-0 ${selected.length > 0 ? 'text-slate-900' : 'text-slate-400'}`} />
          <span className="truncate">
            {selected.length === 0
              ? 'All Departments'
              : selected.length === 1
                ? selected[0]
                : `${selected.length} Depts`}
          </span>
        </div>
        <ChevronDown className="w-3 h-3 text-slate-400 shrink-0" />
      </button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-1.5 z-50 bg-white border border-slate-200 rounded-2xl shadow-2xl w-64 p-2.5 space-y-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search departments..."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-8 pr-3 py-1.5 text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>
          <div className="px-2.5 py-1.5 border-b border-slate-100 flex items-center justify-between">
            <div
              onClick={() => {
                const isAllSelected = filtered.length > 0 && filtered.every((v) => selected.includes(v));
                if (isAllSelected) {
                  setSelected(selected.filter((v) => !filtered.includes(v)));
                } else {
                  setSelected(Array.from(new Set([...selected, ...filtered])));
                }
              }}
              className="flex items-center space-x-3 text-xs cursor-pointer"
            >
              <input
                type="checkbox"
                checked={allDepartments.length > 0 && allDepartments.every((c) => selected.includes(c))}
                readOnly
                className="w-4 h-4 rounded border-slate-300 accent-blue-600 pointer-events-none shrink-0"
              />
              <span className="font-extrabold text-slate-900 text-xs">Select All</span>
            </div>
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => setSelected([])}
                className="text-[10px] font-bold text-rose-600 hover:underline cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>
          <div className="max-h-52 overflow-y-auto space-y-0.5 pr-1">
            {filtered.map((c) => {
              const isChecked = selected.includes(c);
              return (
                <div
                  key={c}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected(isChecked ? selected.filter((v) => v !== c) : [...selected, c]);
                  }}
                  className={`flex items-center space-x-3 px-2.5 py-1.5 rounded-xl text-xs cursor-pointer ${isChecked
                    ? 'bg-slate-50 text-slate-900 font-bold'
                    : 'text-slate-700 font-medium hover:bg-slate-50'
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    readOnly
                    className="w-4 h-4 rounded border-slate-300 accent-blue-600 pointer-events-none shrink-0"
                  />
                  <span className="truncate">{c}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function DistMetrixCard({
  title,
  filterId,
  selectedDepts,
  setSelectedDepts,
  deptSearch,
  setDeptSearch,
  deptRef,
  cols,
  setCols,
  isColsOpen,
  setIsColsOpen,
  colsRef,
  forceHidden,
  tableRows,
  sort,
  setSort,
  search,
  setSearch,
  rowKeyPrefix = '',
  primaryField = 'user',
  allDepartments,
  openDeptFilter,
  setOpenDeptFilter,
}: {
  title: string;
  filterId: 'dist' | 'user' | 'dept';
  selectedDepts: string[];
  setSelectedDepts: (v: string[]) => void;
  deptSearch: string;
  setDeptSearch: (v: string) => void;
  deptRef: React.RefObject<HTMLDivElement | null>;
  cols: Record<DistColKey, boolean>;
  setCols: React.Dispatch<React.SetStateAction<Record<DistColKey, boolean>>>;
  isColsOpen: boolean;
  setIsColsOpen: (v: boolean) => void;
  colsRef: React.RefObject<HTMLDivElement | null>;
  forceHidden: DistColKey[];
  tableRows: MatrixRow[];
  sort: SortDir;
  setSort: (v: SortDir) => void;
  search: string;
  setSearch: (v: string) => void;
  rowKeyPrefix?: string;
  primaryField?: 'user' | 'department';
  allDepartments: string[];
  openDeptFilter: 'dist' | 'user' | 'dept' | null;
  setOpenDeptFilter: (v: 'dist' | 'user' | 'dept' | null) => void;
}) {
  const visibleCols: Record<DistColKey, boolean> = { ...cols };
  if (filterId === 'user') {
    visibleCols.email = cols.email !== false;
  }
  for (const key of forceHidden) visibleCols[key] = false;
  const primaryLabel = primaryField === 'department' ? 'Department Name' : 'User';
  const colOptions = DIST_COL_OPTIONS.filter((c) => !forceHidden.includes(c.key)).map((c) =>
    c.key === 'user' ? { ...c, label: primaryLabel } : c
  );
  const visibleCount = Object.values(visibleCols).filter(Boolean).length || 1;

  const displayedRows = tableRows;
  const totalIdeasCount = tableRows.reduce((s, r) => s + (r.ideasCount || 1), 0);

  const filterSignature = `${sort}|${search}|${selectedDepts.join('|')}`;
  const [isFiltering, setIsFiltering] = useState(false);
  const skipFilterLoader = useRef(true);

  useEffect(() => {
    if (skipFilterLoader.current) {
      skipFilterLoader.current = false;
      return;
    }
    setIsFiltering(true);
    const t = window.setTimeout(() => setIsFiltering(false), 280);
    return () => window.clearTimeout(t);
  }, [filterSignature]);

  return (
    <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 border-b border-slate-100 pb-3">
        <h1 className="text-2xl font-black text-slate-900 tracking-tight">{title}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <DeptFilter
            id={filterId}
            selected={selectedDepts}
            setSelected={setSelectedDepts}
            search={deptSearch}
            setSearch={setDeptSearch}
            containerRef={deptRef}
            allDepartments={allDepartments}
            openDeptFilter={openDeptFilter}
            setOpenDeptFilter={setOpenDeptFilter}
          />
          <div ref={colsRef} className="relative">
            <button
              type="button"
              onClick={() => setIsColsOpen(!isColsOpen)}
              className="border border-slate-200 bg-white hover:bg-slate-50 rounded-xl px-2.5 py-1.5 text-[10px] font-bold text-slate-700 cursor-pointer shadow-sm flex items-center gap-1.5"
            >
              <Filter className="w-3 h-3 text-slate-400" />
              <span>Columns</span>
              <ChevronDown className="w-3 h-3 text-slate-400" />
            </button>
            {isColsOpen && (
              <div className="absolute right-0 top-full mt-1.5 z-50 bg-white border border-slate-200 rounded-xl shadow-xl w-56 p-2 space-y-0.5">
                {colOptions.map((col) => (
                  <label
                    key={col.key}
                    className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer text-[11px] font-semibold text-slate-700"
                  >
                    <input
                      type="checkbox"
                      checked={visibleCols[col.key]}
                      onChange={(e) => setCols((prev) => ({ ...prev, [col.key]: e.target.checked }))}
                      className="w-3.5 h-3.5 rounded border-slate-300 accent-slate-900 cursor-pointer"
                    />
                    <span>{col.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <SortToggle value={sort} onChange={setSort} />
          <div className="relative w-full sm:w-56">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                primaryField === 'department' ? 'Search dept, amount...' : 'Search user, email, dept, amount...'
              }
              className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-[11px] font-medium text-slate-700 outline-none focus:border-slate-400"
            />
          </div>
        </div>
      </div>
      <div className="relative overflow-auto max-h-[550px] border border-slate-200 rounded-2xl shadow-sm">
        {isFiltering && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/70 backdrop-blur-[1px] rounded-2xl">
            <Loader2 className="w-5 h-5 text-slate-500 animate-spin" />
          </div>
        )}
        <table className="w-full text-left text-xs border-collapse">
          <thead className="sticky top-0 z-20">
            <tr className="bg-slate-900 text-white font-bold tracking-wider">
              {visibleCols.user && (
                <th className="p-3.5 uppercase text-center border-r border-slate-800">{primaryLabel}</th>
              )}
              {visibleCols.email && (
                <th className="p-3.5 uppercase text-center border-r border-slate-800">Email</th>
              )}
              {visibleCols.achieved && (
                <th className="p-3.5 uppercase text-center border-r border-slate-800">Achieved Value</th>
              )}
              {visibleCols.company80 && (
                <th className="p-3.5 uppercase text-center border-r border-slate-800">80% Company</th>
              )}
              {visibleCols.totalKitty && (
                <th className="p-3.5 uppercase text-center border-r border-slate-800">Total Kitty Pool (20%)</th>
              )}
              {visibleCols.userKitty && (
                <th className="p-3.5 uppercase text-center border-r border-slate-800">Contributor Share (15%)</th>
              )}
              {visibleCols.deptKitty && (
                <th className="p-3.5 uppercase text-center border-r border-slate-800">Department Kitty (15%)</th>
              )}
              {visibleCols.companyKitty && (
                <th className="p-3.5 uppercase text-center">Company Kitty (70%)</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white font-medium text-slate-700">
            {displayedRows.length === 0 ? (
              <tr>
                <td colSpan={visibleCount} className="p-8 text-center text-slate-400 font-medium">
                  No approved ideas found
                </td>
              </tr>
            ) : (
              displayedRows.map((r, idx) => (
                <tr
                  key={`${rowKeyPrefix}${r.id}`}
                  className={idx % 2 === 0 ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/50 hover:bg-slate-50'}
                >
                  {visibleCols.user && (
                    <td className="p-3.5 text-center border-r border-slate-200">
                      {primaryField === 'department' ? (
                        <span className="font-semibold text-slate-800">{r.department}</span>
                      ) : (
                        <div className="flex flex-col items-center justify-center">
                          <span className="font-semibold text-slate-800 text-xs">{r.userName || '—'}</span>
                          {r.userEmail && (
                            <span className="text-[11px] font-normal text-slate-500 mt-0.5">{r.userEmail}</span>
                          )}
                        </div>
                      )}
                    </td>
                  )}
                  {visibleCols.achieved && (
                    <td className="p-3.5 text-center border-r border-slate-200 font-bold">
                      <MetrixAmountDisplay
                        amount={r.approvedAmount}
                        label="Exact Achieved Value"
                        textSize="text-xs font-bold"
                        textColor="text-slate-800"
                        forceCrores={true}
                      />
                    </td>
                  )}
                  {visibleCols.company80 && (
                    <td className="p-3.5 text-center border-r border-slate-200 font-bold">
                      <MetrixAmountDisplay
                        amount={r.companyDirectAmount}
                        label="Exact 80% Company"
                        textSize="text-xs font-bold"
                        textColor="text-slate-700"
                        forceCrores={true}
                      />
                    </td>
                  )}
                  {visibleCols.totalKitty && (
                    <td className="p-3.5 text-center border-r border-slate-200 font-bold">
                      <MetrixAmountDisplay
                        amount={r.incentivePoolAmount}
                        label="Exact Total Kitty Pool (20%)"
                        textSize="text-xs font-bold"
                        textColor="text-amber-700"
                        forceCrores={true}
                      />
                    </td>
                  )}
                  {visibleCols.userKitty && (
                    <td className="p-3.5 text-center border-r border-slate-200 font-bold">
                      <MetrixAmountDisplay
                        amount={r.commissionAmount}
                        label="Exact Contributor Share (15%)"
                        textSize="text-xs font-bold"
                        textColor="text-emerald-700"
                        forceCrores={true}
                      />
                    </td>
                  )}
                  {visibleCols.deptKitty && (
                    <td className="p-3.5 text-center border-r border-slate-200 font-bold">
                      <MetrixAmountDisplay
                        amount={r.departmentAmount}
                        label="Exact Department Kitty (15%)"
                        textSize="text-xs font-bold"
                        textColor="text-blue-700"
                        forceCrores={true}
                      />
                    </td>
                  )}
                  {visibleCols.companyKitty && (
                    <td className="p-3.5 text-center font-bold">
                      <MetrixAmountDisplay
                        amount={r.companyPoolShareAmount}
                        label="Exact Company Kitty (70%)"
                        textSize="text-xs font-bold"
                        textColor="text-slate-800"
                        forceCrores={true}
                      />
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
          {displayedRows.length > 0 && (
            <tfoot className="sticky bottom-0 z-20">
              <tr className="bg-slate-100 text-slate-900 font-black border-t-2 border-slate-300 shadow-[0_-4px_6px_-2px_rgba(0,0,0,0.08)]">
                {visibleCols.user && (
                  <td className="p-3.5 border-r border-slate-300 uppercase tracking-wider text-blue-950 text-center bg-slate-100">
                    TOTAL ({totalIdeasCount} Ideas)
                  </td>
                )}
                {visibleCols.email && (
                  <td className="p-3.5 border-r border-slate-300 bg-slate-100"></td>
                )}
                {visibleCols.achieved && (
                  <td className="p-3.5 text-center border-r border-slate-300 bg-slate-100">
                    <MetrixAmountDisplay
                      amount={tableRows.reduce((s, r) => s + r.approvedAmount, 0)}
                      label="Total Achieved Value"
                      textSize="text-xs font-black"
                      textColor="text-slate-800"
                      forceCrores={true}
                    />
                  </td>
                )}
                {visibleCols.company80 && (
                  <td className="p-3.5 text-center border-r border-slate-300 bg-slate-100">
                    <MetrixAmountDisplay
                      amount={tableRows.reduce((s, r) => s + r.companyDirectAmount, 0)}
                      label="Total 80% Company"
                      textSize="text-xs font-black"
                      textColor="text-slate-800"
                      forceCrores={true}
                    />
                  </td>
                )}
                {visibleCols.totalKitty && (
                  <td className="p-3.5 text-center border-r border-slate-300 bg-slate-100">
                    <MetrixAmountDisplay
                      amount={tableRows.reduce((s, r) => s + r.incentivePoolAmount, 0)}
                      label="Total Kitty Pool (20%)"
                      textSize="text-xs font-black"
                      textColor="text-amber-700"
                      forceCrores={true}
                    />
                  </td>
                )}
                {visibleCols.userKitty && (
                  <td className="p-3.5 text-center border-r border-slate-300 bg-slate-100">
                    <MetrixAmountDisplay
                      amount={tableRows.reduce((s, r) => s + r.commissionAmount, 0)}
                      label="Total Contributor Share (15%)"
                      textSize="text-xs font-black"
                      textColor="text-emerald-700"
                      forceCrores={true}
                    />
                  </td>
                )}
                {visibleCols.deptKitty && (
                  <td className="p-3.5 text-center border-r border-slate-300 bg-slate-100">
                    <MetrixAmountDisplay
                      amount={tableRows.reduce((s, r) => s + r.departmentAmount, 0)}
                      label="Total Department Kitty (15%)"
                      textSize="text-xs font-black"
                      textColor="text-blue-700"
                      forceCrores={true}
                    />
                  </td>
                )}
                {visibleCols.companyKitty && (
                  <td className="p-3.5 text-center bg-slate-100">
                    <MetrixAmountDisplay
                      amount={tableRows.reduce((s, r) => s + r.companyPoolShareAmount, 0)}
                      label="Total Company Kitty (70%)"
                      textSize="text-xs font-black"
                      textColor="text-slate-800"
                      forceCrores={true}
                    />
                  </td>
                )}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

/** Group rows by User identity (userId / userEmail / userName) */
function groupRowsByUser(rawRows: MatrixRow[]): MatrixRow[] {
  const map = new Map<
    string,
    {
      userId: string;
      userName: string;
      userEmail: string;
      depts: Set<string>;
      ideaNames: string[];
      approvedAmount: number;
      companyDirectAmount: number;
      incentivePoolAmount: number;
      commissionAmount: number;
      departmentAmount: number;
      companyPoolShareAmount: number;
      companyTotalAmount: number;
      ideasCount: number;
    }
  >();

  for (const r of rawRows) {
    // If the name is the same but email or ID differs, keep them separate!
    const key = r.userId?.trim()
      ? `${r.userId.trim()}__${(r.userEmail || '').trim().toLowerCase()}`
      : r.userEmail?.trim()
        ? `email__${r.userEmail.trim().toLowerCase()}`
        : `name__${r.userName.trim().toLowerCase()}`;

    const existing = map.get(key);
    if (existing) {
      if (r.department) existing.depts.add(r.department);
      if (r.ideaName) existing.ideaNames.push(r.ideaName);
      existing.approvedAmount += r.approvedAmount;
      existing.companyDirectAmount += r.companyDirectAmount;
      existing.incentivePoolAmount += r.incentivePoolAmount;
      existing.commissionAmount += r.commissionAmount;
      existing.departmentAmount += r.departmentAmount;
      existing.companyPoolShareAmount += r.companyPoolShareAmount;
      existing.companyTotalAmount += r.companyTotalAmount;
      existing.ideasCount += 1;
      if (!existing.userEmail && r.userEmail) {
        existing.userEmail = r.userEmail;
      }
    } else {
      map.set(key, {
        userId: r.userId,
        userName: r.userName,
        userEmail: r.userEmail || '',
        depts: new Set(r.department ? [r.department] : []),
        ideaNames: r.ideaName ? [r.ideaName] : [],
        approvedAmount: r.approvedAmount,
        companyDirectAmount: r.companyDirectAmount,
        incentivePoolAmount: r.incentivePoolAmount,
        commissionAmount: r.commissionAmount,
        departmentAmount: r.departmentAmount,
        companyPoolShareAmount: r.companyPoolShareAmount,
        companyTotalAmount: r.companyTotalAmount,
        ideasCount: 1,
      });
    }
  }

  return Array.from(map.entries()).map(([key, item]) => ({
    id: `grouped-user-${key}`,
    userId: item.userId,
    userName: item.userName,
    userEmail: item.userEmail,
    ideaName: item.ideaNames.join(', '),
    department: Array.from(item.depts).join(', ') || 'Unassigned',
    approvedAmount: item.approvedAmount,
    companyDirectAmount: item.companyDirectAmount,
    incentivePoolAmount: item.incentivePoolAmount,
    commissionAmount: item.commissionAmount,
    departmentAmount: item.departmentAmount,
    companyPoolShareAmount: item.companyPoolShareAmount,
    companyTotalAmount: item.companyTotalAmount,
    ideasCount: item.ideasCount,
  }));
}

/** Group rows by Department Name */
function groupRowsByDepartment(rawRows: MatrixRow[]): MatrixRow[] {
  const map = new Map<
    string,
    {
      department: string;
      users: Set<string>;
      ideaNames: string[];
      approvedAmount: number;
      companyDirectAmount: number;
      incentivePoolAmount: number;
      commissionAmount: number;
      departmentAmount: number;
      companyPoolShareAmount: number;
      companyTotalAmount: number;
      ideasCount: number;
    }
  >();

  for (const r of rawRows) {
    const deptName = (r.department || 'Unassigned').trim();
    const key = deptName.toLowerCase();

    const existing = map.get(key);
    if (existing) {
      if (r.userName) existing.users.add(r.userName);
      if (r.ideaName) existing.ideaNames.push(r.ideaName);
      existing.approvedAmount += r.approvedAmount;
      existing.companyDirectAmount += r.companyDirectAmount;
      existing.incentivePoolAmount += r.incentivePoolAmount;
      existing.commissionAmount += r.commissionAmount;
      existing.departmentAmount += r.departmentAmount;
      existing.companyPoolShareAmount += r.companyPoolShareAmount;
      existing.companyTotalAmount += r.companyTotalAmount;
      existing.ideasCount += 1;
    } else {
      map.set(key, {
        department: deptName,
        users: new Set(r.userName ? [r.userName] : []),
        ideaNames: r.ideaName ? [r.ideaName] : [],
        approvedAmount: r.approvedAmount,
        companyDirectAmount: r.companyDirectAmount,
        incentivePoolAmount: r.incentivePoolAmount,
        commissionAmount: r.commissionAmount,
        departmentAmount: r.departmentAmount,
        companyPoolShareAmount: r.companyPoolShareAmount,
        companyTotalAmount: r.companyTotalAmount,
        ideasCount: 1,
      });
    }
  }

  return Array.from(map.entries()).map(([key, item]) => ({
    id: `grouped-dept-${key}`,
    userId: '',
    userName: item.department,
    userEmail: '',
    ideaName: item.ideaNames.join(', '),
    department: item.department,
    approvedAmount: item.approvedAmount,
    companyDirectAmount: item.companyDirectAmount,
    incentivePoolAmount: item.incentivePoolAmount,
    commissionAmount: item.commissionAmount,
    departmentAmount: item.departmentAmount,
    companyPoolShareAmount: item.companyPoolShareAmount,
    companyTotalAmount: item.companyTotalAmount,
    ideasCount: item.ideasCount,
  }));
}


export function MatrixPage() {
  const [distDepts, setDistDepts] = useState<string[]>([]);
  const [userDepts, setUserDepts] = useState<string[]>([]);
  const [tableDepts, setTableDepts] = useState<string[]>([]);
  const [openDeptFilter, setOpenDeptFilter] = useState<'dist' | 'user' | 'dept' | null>(null);
  const [distDeptSearch, setDistDeptSearch] = useState('');
  const [userDeptSearch, setUserDeptSearch] = useState('');
  const [tableDeptSearch, setTableDeptSearch] = useState('');
  const distDeptRef = useRef<HTMLDivElement>(null);
  const userDeptRef = useRef<HTMLDivElement>(null);
  const tableDeptRef = useRef<HTMLDivElement>(null);

  const [distSearch, setDistSearch] = useState('');
  const [distSort, setDistSort] = useState<SortDir>('DESC');
  const [distCols, setDistCols] = useState<Record<DistColKey, boolean>>(DEFAULT_DIST_COLS);
  const [isDistColsOpen, setIsDistColsOpen] = useState(false);
  const distColsRef = useRef<HTMLDivElement>(null);
  const [userCols, setUserCols] = useState<Record<DistColKey, boolean>>(DEFAULT_USER_DIST_COLS);
  const [isUserColsOpen, setIsUserColsOpen] = useState(false);
  const userColsRef = useRef<HTMLDivElement>(null);
  const [userSearch, setUserSearch] = useState('');
  const [userSort, setUserSort] = useState<SortDir>('DESC');
  const [deptCols, setDeptCols] = useState<Record<DistColKey, boolean>>(DEFAULT_DEPT_DIST_COLS);
  const [isDeptColsOpen, setIsDeptColsOpen] = useState(false);
  const deptColsRef = useRef<HTMLDivElement>(null);
  const [deptTableSearch, setDeptTableSearch] = useState('');
  const [deptSort, setDeptSort] = useState<SortDir>('DESC');

  useEffect(() => {
    setUserCols((prev) => ({ ...prev, email: true }));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      if (openDeptFilter === 'dist' && distDeptRef.current && !distDeptRef.current.contains(targetNode)) {
        setOpenDeptFilter(null);
      }
      if (openDeptFilter === 'user' && userDeptRef.current && !userDeptRef.current.contains(targetNode)) {
        setOpenDeptFilter(null);
      }
      if (openDeptFilter === 'dept' && tableDeptRef.current && !tableDeptRef.current.contains(targetNode)) {
        setOpenDeptFilter(null);
      }
      if (distColsRef.current && !distColsRef.current.contains(targetNode)) {
        setIsDistColsOpen(false);
      }
      if (userColsRef.current && !userColsRef.current.contains(targetNode)) {
        setIsUserColsOpen(false);
      }
      if (deptColsRef.current && !deptColsRef.current.contains(targetNode)) {
        setIsDeptColsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDeptFilter]);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['approvalAmountMatrix'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/reports/approval-matrix');
      return res.data.data as MatrixData;
    },
  });

  const rows = useMemo(() => {
    const raw = data?.rows ?? [];
    return raw.map((r) => {
      if (
        typeof r.companyDirectAmount === 'number' &&
        typeof r.incentivePoolAmount === 'number' &&
        typeof r.companyPoolShareAmount === 'number'
      ) {
        return r;
      }
      const incentivePoolAmount = Math.round(r.approvedAmount * 0.2);
      const companyDirectAmount = r.approvedAmount - incentivePoolAmount;
      const commissionAmount = r.commissionAmount ?? Math.round(incentivePoolAmount * 0.15);
      const departmentAmount = r.departmentAmount ?? Math.round(incentivePoolAmount * 0.15);
      const companyPoolShareAmount = incentivePoolAmount - commissionAmount - departmentAmount;
      return {
        ...r,
        companyDirectAmount,
        incentivePoolAmount,
        commissionAmount,
        departmentAmount,
        companyPoolShareAmount,
        companyTotalAmount: companyDirectAmount + companyPoolShareAmount,
      };
    });
  }, [data?.rows]);
  const departmentRows = data?.departments ?? [];

  const allDepartments = useMemo(() => {
    const fromDepts = departmentRows.map((d) => d.department);
    const fromRows = rows.map((r) => r.department);
    return Array.from(new Set([...fromDepts, ...fromRows])).filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [departmentRows, rows]);

  const filterAndGroupRows = (
    list: MatrixRow[],
    depts: string[],
    search: string,
    sort: SortDir,
    groupBy: 'user' | 'department'
  ) => {
    let scoped = list;
    if (depts.length > 0) {
      scoped = scoped.filter((r) => depts.includes(r.department));
    }

    const grouped = groupBy === 'department' ? groupRowsByDepartment(scoped) : groupRowsByUser(scoped);

    const q = search.trim().toLowerCase();
    let result = grouped;
    if (q) {
      result = result.filter((r) => {
        const text = [
          r.userName,
          r.userEmail || '',
          r.department,
          r.ideaName,
          amountSearchText(
            r.approvedAmount,
            r.companyDirectAmount,
            r.incentivePoolAmount,
            r.commissionAmount,
            r.departmentAmount,
            r.companyPoolShareAmount
          ),
        ]
          .join(' ')
          .toLowerCase();
        return text.includes(q);
      });
    }

    return [...result].sort((a, b) =>
      sort === 'ASC'
        ? a.incentivePoolAmount - b.incentivePoolAmount
        : b.incentivePoolAmount - a.incentivePoolAmount
    );
  };

  const distributionRows = useMemo(
    () => filterAndGroupRows(rows, distDepts, distSearch, distSort, 'user'),
    [rows, distDepts, distSearch, distSort]
  );

  const userRows = useMemo(
    () => filterAndGroupRows(rows, userDepts, userSearch, userSort, 'user'),
    [rows, userDepts, userSearch, userSort]
  );

  const deptDistRows = useMemo(
    () => filterAndGroupRows(rows, tableDepts, deptTableSearch, deptSort, 'department'),
    [rows, tableDepts, deptTableSearch, deptSort]
  );

  /* OLD department-wise aggregated table (kept for reference)
  const departmentTableRows = useMemo(() => {
    const scoped = tableDepts.length > 0 ? rows.filter((r) => tableDepts.includes(r.department)) : rows;
    const map = new Map<string, { userIds: Set<string>; amount: number; ideasCount: number }>();
    for (const r of scoped) {
      const existing = map.get(r.department);
      if (existing) {
        existing.userIds.add(r.userId);
        existing.amount += r.departmentAmount;
        existing.ideasCount += 1;
      } else {
        map.set(r.department, {
          userIds: new Set([r.userId]),
          amount: r.departmentAmount,
          ideasCount: 1,
        });
      }
    }
    const sourceDepts = tableDepts.length > 0 ? tableDepts : departmentRows.map((d) => d.department);
    const uniqueDepts = Array.from(new Set(sourceDepts)).sort((a, b) => a.localeCompare(b));
    let list = uniqueDepts.map((department) => {
      const agg = map.get(department);
      if (agg) {
        return { department, usersCount: agg.userIds.size, ideasCount: agg.ideasCount, amount: agg.amount };
      }
      const fallback = departmentRows.find((d) => d.department === department);
      return {
        department,
        usersCount: fallback?.usersCount ?? 0,
        ideasCount: fallback?.ideasCount ?? 0,
        amount: fallback?.amount ?? 0,
      };
    });
    const q = deptTableSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((r) => {
        const text = [r.department, String(r.usersCount), amountSearchText(r.amount)].join(' ').toLowerCase();
        return text.includes(q);
      });
    }
    return [...list].sort((a, b) => (deptSort === 'ASC' ? a.amount - b.amount : b.amount - a.amount));
  }, [rows, tableDepts, departmentRows, deptTableSearch, deptSort]);
  */

  const summary = useMemo(() => {
    const totalApproved = rows.reduce((s, r) => s + r.approvedAmount, 0);
    const totalPool = rows.reduce((s, r) => s + r.incentivePoolAmount, 0);
    const totalUserKitty = rows.reduce((s, r) => s + r.commissionAmount, 0);
    const totalDeptKitty = rows.reduce((s, r) => s + r.departmentAmount, 0);
    const totalCompanyKitty = rows.reduce((s, r) => s + r.companyPoolShareAmount, 0);
    return {
      totalApproved,
      totalPool,
      totalUserKitty,
      totalDeptKitty,
      totalCompanyKitty,
    };
  }, [rows]);


  return (
    <Layout title="DOM 680 Incentive Dashboard">
      <div className="flex flex-col gap-2">
        {/* Sticky top sub-header banner card removed as requested */}

        {isLoading ? (
          <div className="bg-white p-16 rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center justify-center gap-3">
            <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
            <p className="text-sm font-medium text-slate-500">Loading incentive dashboard…</p>
          </div>
        ) : isError ? (
          <div className="bg-white p-10 rounded-2xl border border-red-200 shadow-sm flex flex-col items-center gap-3">
            <p className="text-sm font-bold text-red-600">Failed to load matrix data</p>
            <p className="text-xs text-slate-500">{(error as Error)?.message || 'Unknown error'}</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-xl cursor-pointer"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* KPI summary */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
              <div className="mb-4 border-b border-slate-100 pb-3 flex items-start justify-between gap-3">
                <h1 className="text-2xl font-black text-slate-900 tracking-tight">DOM 680 | DASHBOARD</h1>
                {isFetching && <Loader2 className="w-4 h-4 text-slate-400 animate-spin shrink-0 mt-1" />}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="bg-white p-3.5 sm:p-5 rounded-2xl border border-slate-200 min-w-0 overflow-hidden flex flex-col justify-between shadow-xs">
                  <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide truncate">
                    Approved Savings Value
                  </p>
                  <div className="my-2">
                    <MetrixAmountDisplay
                      amount={summary.totalApproved}
                      label="Full Approved Savings"
                      textSize="text-[30px] font-black tracking-tight leading-none"
                      textColor="text-slate-900"
                      align="left"
                    />
                  </div>
                  <p className="text-xs font-semibold text-slate-400 flex items-center gap-1.5 truncate">
                    <Shield className="w-3.5 h-3.5 shrink-0" />
                    <span>Base Verified (100%)</span>
                  </p>
                </div>

                <div className="bg-white p-3.5 sm:p-5 rounded-2xl border border-slate-200 min-w-0 overflow-hidden flex flex-col justify-between shadow-xs">
                  <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide truncate">Total Kitty Pool</p>
                  <div className="my-2">
                    <MetrixAmountDisplay
                      amount={summary.totalPool}
                      label="Full Total Kitty Pool"
                      textSize="text-[30px] font-black tracking-tight leading-none"
                      textColor="text-blue-600"
                      align="left"
                    />
                  </div>
                  <p className="text-xs font-semibold text-slate-400 truncate">20% of Approved Value</p>
                </div>

                <div className="bg-white p-3.5 sm:p-5 rounded-2xl border border-slate-200 min-w-0 overflow-hidden flex flex-col justify-between shadow-xs">
                  <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide truncate">Contributor Share</p>
                  <div className="my-2">
                    <MetrixAmountDisplay
                      amount={summary.totalUserKitty}
                      label="Full Contributor Share"
                      textSize="text-[30px] font-black tracking-tight leading-none"
                      textColor="text-slate-900"
                      align="left"
                    />
                  </div>
                  <p className="text-xs font-semibold text-slate-400 truncate">15% of Kitty Pool (3%)</p>
                </div>

                <div className="bg-white p-3.5 sm:p-5 rounded-2xl border border-slate-200 min-w-0 overflow-hidden flex flex-col justify-between shadow-xs">
                  <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide truncate">Department Kitty</p>
                  <div className="my-2">
                    <MetrixAmountDisplay
                      amount={summary.totalDeptKitty}
                      label="Full Department Kitty"
                      textSize="text-[30px] font-black tracking-tight leading-none"
                      textColor="text-slate-900"
                      align="left"
                    />
                  </div>
                  <p className="text-xs font-semibold text-slate-400 truncate">15% of Kitty Pool (3%)</p>
                </div>

                <div className="bg-white p-3.5 sm:p-5 rounded-2xl border border-slate-200 min-w-0 overflow-hidden flex flex-col justify-between shadow-xs">
                  <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide truncate">Company Kitty</p>
                  <div className="my-2">
                    <MetrixAmountDisplay
                      amount={summary.totalCompanyKitty}
                      label="Full Company Kitty"
                      textSize="text-[30px] font-black tracking-tight leading-none"
                      textColor="text-emerald-600"
                      align="left"
                    />
                  </div>
                  <p className="text-xs font-semibold text-slate-400 truncate">70% of Kitty Pool (14%)</p>
                </div>
              </div>
            </div>

            <DistMetrixCard
              title="Overall Summary"
              filterId="dist"
              selectedDepts={distDepts}
              setSelectedDepts={setDistDepts}
              deptSearch={distDeptSearch}
              setDeptSearch={setDistDeptSearch}
              deptRef={distDeptRef}
              cols={distCols}
              setCols={setDistCols}
              isColsOpen={isDistColsOpen}
              setIsColsOpen={setIsDistColsOpen}
              colsRef={distColsRef}
              allDepartments={allDepartments}
              openDeptFilter={openDeptFilter}
              setOpenDeptFilter={setOpenDeptFilter}
              forceHidden={['email']}
              tableRows={distributionRows}
              sort={distSort}
              setSort={setDistSort}
              search={distSearch}
              setSearch={setDistSearch}
            />

            <DistMetrixCard
              title="User Wise Earning"
              filterId="user"
              selectedDepts={userDepts}
              setSelectedDepts={setUserDepts}
              deptSearch={userDeptSearch}
              setDeptSearch={setUserDeptSearch}
              deptRef={userDeptRef}
              cols={userCols}
              setCols={setUserCols}
              isColsOpen={isUserColsOpen}
              setIsColsOpen={setIsUserColsOpen}
              colsRef={userColsRef}
              allDepartments={allDepartments}
              openDeptFilter={openDeptFilter}
              setOpenDeptFilter={setOpenDeptFilter}
              forceHidden={['email', 'deptKitty']}
              tableRows={userRows}
              sort={userSort}
              setSort={setUserSort}
              search={userSearch}
              setSearch={setUserSearch}
              rowKeyPrefix="user-"
            />

            <DistMetrixCard
              title="Department Wise Earning"
              filterId="dept"
              selectedDepts={tableDepts}
              setSelectedDepts={setTableDepts}
              deptSearch={tableDeptSearch}
              setDeptSearch={setTableDeptSearch}
              deptRef={tableDeptRef}
              cols={deptCols}
              setCols={setDeptCols}
              isColsOpen={isDeptColsOpen}
              setIsColsOpen={setIsDeptColsOpen}
              colsRef={deptColsRef}
              allDepartments={allDepartments}
              openDeptFilter={openDeptFilter}
              setOpenDeptFilter={setOpenDeptFilter}
              forceHidden={['email', 'userKitty']}
              tableRows={deptDistRows}
              sort={deptSort}
              setSort={setDeptSort}
              search={deptTableSearch}
              setSearch={setDeptTableSearch}
              rowKeyPrefix="dept-"
              primaryField="department"
            />

            {/* OLD User-Wise / Department-Wise tables kept for reference
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
              <h1>User-Wise Commission Metrix (OLD)</h1>
              ... User Name / Department / Approved Value / User Kitty table ...
            </div>
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
              <h1>Department-Wise Achievement Metrix (OLD)</h1>
              ... Department Name / Users Count / Amount table ...
            </div>
            */}
          </>
        )}
      </div>
    </Layout>
  );
}
